import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import {
    Grid,
    Typography,
    Paper,
    IconButton,
    Link,
    Button,
    CircularProgress,
    TextField,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Radio,
    Breadcrumbs,
    Divider
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import OpacityOutlinedIcon from '@material-ui/icons/OpacityOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';
import AccountBalanceOutlinedIcon from '@material-ui/icons/AccountBalanceOutlined';
import NotesOutlinedIcon from '@material-ui/icons/NotesOutlined';
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import { getExtension } from 'common/validators';
import { getConfig, setConfig } from './http';
import FileInput from './components/FileInput';

const useStyles = makeStyles(theme => ({
    '@global': {
        '.MuiOutlinedInput-root': {
            backgroundColor: '#FAFAFB'
        }
    },
    page: {
        padding: theme.spacing(4, 2, 8)
    },
    pageInner: {
        maxWidth: 960,
        margin: '0 auto'
    },
    breadcrumbRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2)
    },
    backButton: {
        backgroundColor: '#fff',
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.08)',
        '&:hover': {
            backgroundColor: '#fff'
        }
    },
    breadcrumbLink: {
        color: '#6B7280',
        fontWeight: 500,
        '&:hover': {
            color: '#3f51b5'
        }
    },
    breadcrumbCurrent: {
        color: '#111827',
        fontWeight: 600
    },
    pageHeader: {
        marginBottom: theme.spacing(3)
    },
    pageTitle: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
        color: '#111827'
    },
    pageSubtitle: {
        color: '#6B7280',
        marginTop: theme.spacing(0.5)
    },
    paper: {
        borderRadius: 8,
        overflow: 'hidden'
    },
    section: {
        padding: theme.spacing(4)
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2.5)
    },
    sectionHeaderText: {
        flex: 1,
        minWidth: 0
    },
    sectionIcon: {
        width: 40,
        height: 40,
        minWidth: 40,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(63, 81, 181, 0.1)',
        color: '#3f51b5'
    },
    sectionTitle: {
        fontWeight: 600,
        fontSize: '1.05rem',
        color: '#111827'
    },
    sectionDescription: {
        color: '#6B7280',
        marginTop: 2
    },
    divider: {
        margin: 0
    },
    segmentGroup: {
        display: 'inline-flex',
        padding: 4,
        backgroundColor: '#F1F2F6',
        borderRadius: 10,
        gap: 4
    },
    segmentOption: {
        margin: 0,
        borderRadius: 8,
        padding: theme.spacing(0.75, 2),
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        color: '#4B5563',
        fontWeight: 500,
        '& .MuiFormControlLabel-label': {
            fontWeight: 500,
            fontSize: '0.9rem'
        },
        '& .MuiRadio-root': {
            display: 'none'
        }
    },
    segmentOptionActive: {
        backgroundColor: '#fff',
        color: '#3f51b5',
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.12)',
        '& .MuiFormControlLabel-label': {
            color: '#3f51b5',
            fontWeight: 600
        }
    },
    dropZone: {
        border: '1.5px dashed #D1D5DB',
        borderRadius: 12,
        padding: theme.spacing(3),
        backgroundColor: '#FAFAFB'
    },
    previewContainer: {
        marginTop: theme.spacing(2),
        border: '1px solid #E5E7EB',
        borderRadius: 12,
        padding: theme.spacing(2),
        backgroundColor: '#fff'
    },
    previewImg: {
        width: '100%',
        maxHeight: 160,
        objectFit: 'contain',
        borderRadius: 8
    },
    footer: {
        padding: theme.spacing(2, 3),
        backgroundColor: '#FAFAFB',
        borderTop: '1px solid #EEF0F4',
        borderBottomRightRadius: 8,
        borderBottomLeftRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(2)
    },
    message: {
        color: '#3f51b5',
        fontWeight: 500
    },
    submitButton: {
        borderRadius: 10,
        padding: theme.spacing(1, 3.5),
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: 'none'
    },
    skeletonRadius: {
        borderRadius: 10
    }
}));

// One skeleton "row" mirrors a real section: icon + title (+ optional
// description) on top, and a control placeholder sized to match what will
// load in (segmented control, textarea, dropzone...).
const SectionSkeleton = ({ classes, withDescription, control }) => (
    <div className={classes.section}>
        <div className={classes.sectionHeader}>
            <Skeleton variant="circle" width={40} height={40} />
            <div className={classes.sectionHeaderText}>
                <Skeleton variant="text" width="45%" height={28} />
                {withDescription && <Skeleton variant="text" width="65%" height={20} />}
            </div>
        </div>
        {control}
    </div>
);

const BillConfig = () => {
    const classes = useStyles();
    const [autoPrintBill, setAutoPrintBill] = useState(false);
    const [billingMethod, setBillingMethod] = useState('j');
    const [billingOptionMethod, setBillingOptionMethod] = useState('j');
    const [bankIdentificationStatement, setBankIdentificationStatement] = useState(null);
    const [additionalInformation, setAdditionalInformation] = useState(null);
    const [signature, setSignature] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState('');
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        getConfig()
            .then(res => {
                setBankIdentificationStatement(res.bank_identification_statement);
                setAdditionalInformation(res.additional_information);
                setSignature(res.signature);
                setAutoPrintBill(res.auto_print_bill);
                setBillingMethod(res.billing_method);
                setBillingOptionMethod(res.billing_option_method);

                if (res.signature) {
                    const output_1 = document.getElementById('output_1');
                    const output_2 = document.getElementById('output_2');
                    output_1.src = res.signature;
                    output_2.href = res.signature;
                }
            })
            .catch(err => {
                console.error(err);
                alert(err);
            })
            .finally(() => setMounted(true));
    }, []);

    useEffect(() => {
        mounted && showPreview();
    }, [signature]);

    const handleChange = (target, value) => {
        target === 'bankIdentificationStatement' && setBankIdentificationStatement(value);
        target === 'additionalInformation' && setAdditionalInformation(value);
    };

    const handleFile = value => {
        if (value) {
            const extension = getExtension(value.name);
            if (extension && (extension == 'png' || extension == 'jpg' || extension == 'jpeg')) {
                setSignature(value);
            } else {
                alert('Vous devez sélectionner une image (png, jpg, jpeg).');
            }
        } else {
            setSignature(null);
        }
    };

    const handleSubmit = e => {
        e.preventDefault();
        setMessage('');
        setProcessing(true);

        setConfig({
            bankIdentificationStatement,
            additionalInformation,
            signature,
            billingMethod,
            autoPrintBill,
            billingOptionMethod
        })
            .then(() => setMessage('Mise à jour effectué avec succès'))
            .catch(err => {
                console.error(err);
                setMessage(err);
            })
            .finally(() => setProcessing(false));
    };

    const showPreview = () => {
        if (signature) {
            const reader = new FileReader();
            reader.onload = function() {
                const output_1 = document.getElementById('output_1');
                output_1.src = reader.result;

                const output_2 = document.getElementById('output_2');
                output_2.href = reader.result;
            };

            reader.readAsDataURL(signature);
        }
    };

    // Small helper to render a two-option segmented control on top of the
    // existing RadioGroup/FormControlLabel primitives (keeps a11y + form semantics).
    const SegmentedField = ({ name, value, onChange, options }) => (
        <FormControl component="fieldset">
            <RadioGroup
                row
                name={name}
                value={value}
                onChange={onChange}
                className={classes.segmentGroup}>
                {options.map(opt => (
                    <FormControlLabel
                        key={String(opt.value)}
                        value={opt.value}
                        control={<Radio color="primary" />}
                        label={opt.label}
                        className={`${classes.segmentOption} ${
                            String(value) === String(opt.value) ? classes.segmentOptionActive : ''
                        }`}
                    />
                ))}
            </RadioGroup>
        </FormControl>
    );

    return (
        <div className={classes.page}>
            <div className={classes.pageInner}>
                {/* Breadcrumb now lives outside the card, as its own nav element */}
                <div className={classes.breadcrumbRow}>
                    <IconButton
                        component={RouterLink}
                        to="/settings"
                        size="small"
                        className={classes.backButton}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Breadcrumbs separator="/">
                        <Link
                            component={RouterLink}
                            to="/settings"
                            className={classes.breadcrumbLink}
                            underline="none">
                            Les paramètres
                        </Link>
                        <Typography className={classes.breadcrumbCurrent}>
                            Configuration de la facturation
                        </Typography>
                    </Breadcrumbs>
                </div>

                <div className={classes.pageHeader}>
                    <Typography variant="h4" className={classes.pageTitle}>
                        Configuration de la facturation
                    </Typography>
                    <Typography variant="body1" className={classes.pageSubtitle}>
                        Gérez les modes de tarification, l'impression automatique et les
                        informations qui apparaissent sur vos factures.
                    </Typography>
                </div>

                {!mounted ? (
                    // ---- Loading skeleton: mirrors the section layout below ----
                    <Paper className={classes.paper} variant="elevation" elevation={1}>
                        <SectionSkeleton
                            classes={classes}
                            control={
                                <Skeleton
                                    variant="rect"
                                    width={220}
                                    height={40}
                                    className={classes.skeletonRadius}
                                />
                            }
                        />
                        <Divider className={classes.divider} />

                        <SectionSkeleton
                            classes={classes}
                            control={
                                <Skeleton
                                    variant="rect"
                                    width={220}
                                    height={40}
                                    className={classes.skeletonRadius}
                                />
                            }
                        />
                        <Divider className={classes.divider} />

                        <SectionSkeleton
                            classes={classes}
                            withDescription
                            control={
                                <Skeleton
                                    variant="rect"
                                    width={140}
                                    height={40}
                                    className={classes.skeletonRadius}
                                />
                            }
                        />
                        <Divider className={classes.divider} />

                        <SectionSkeleton
                            classes={classes}
                            control={
                                <Skeleton
                                    variant="rect"
                                    width="100%"
                                    height={80}
                                    className={classes.skeletonRadius}
                                />
                            }
                        />
                        <Divider className={classes.divider} />

                        <SectionSkeleton
                            classes={classes}
                            control={
                                <Skeleton
                                    variant="rect"
                                    width="100%"
                                    height={190}
                                    className={classes.skeletonRadius}
                                />
                            }
                        />
                        <Divider className={classes.divider} />

                        <SectionSkeleton
                            classes={classes}
                            withDescription
                            control={
                                <Skeleton
                                    variant="rect"
                                    width="100%"
                                    height={96}
                                    className={classes.skeletonRadius}
                                />
                            }
                        />

                        <div className={classes.footer}>
                            <Skeleton
                                variant="rect"
                                width={140}
                                height={40}
                                className={classes.skeletonRadius}
                            />
                        </div>
                    </Paper>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <Paper className={classes.paper} variant="elevation" elevation={1}>
                            {/* Mode de facturation des fluides */}
                            <div className={classes.section}>
                                <div className={classes.sectionHeader}>
                                    <div className={classes.sectionIcon}>
                                        <OpacityOutlinedIcon fontSize="small" />
                                    </div>
                                    <div>
                                        <Typography className={classes.sectionTitle}>
                                            Mode de facturation des fluides
                                        </Typography>
                                    </div>
                                </div>
                                <SegmentedField
                                    name="billingOptionMethod"
                                    value={billingOptionMethod}
                                    onChange={e => setBillingOptionMethod(e.target.value)}
                                    options={[
                                        { value: 'j', label: 'Tarif journalier' },
                                        { value: 'm', label: 'Tarif mensuel' }
                                    ]}
                                />
                            </div>

                            <Divider className={classes.divider} />

                            {/* Mode de facturation des titulaires abonnés */}
                            <div className={classes.section}>
                                <div className={classes.sectionHeader}>
                                    <div className={classes.sectionIcon}>
                                        <PeopleOutlineIcon fontSize="small" />
                                    </div>
                                    <div>
                                        <Typography className={classes.sectionTitle}>
                                            Mode de facturation des titulaires abonnés
                                        </Typography>
                                    </div>
                                </div>
                                <SegmentedField
                                    name="billingMethod"
                                    value={billingMethod}
                                    onChange={e => setBillingMethod(e.target.value)}
                                    options={[
                                        { value: 'j', label: 'Tarif journalier' },
                                        { value: 'm', label: 'Tarif mensuel' }
                                    ]}
                                />
                            </div>

                            <Divider className={classes.divider} />

                            {/* Impression automatique via PAX */}
                            <div className={classes.section}>
                                <div className={classes.sectionHeader}>
                                    <div className={classes.sectionIcon}>
                                        <PrintOutlinedIcon fontSize="small" />
                                    </div>
                                    <div>
                                        <Typography className={classes.sectionTitle}>
                                            Impression automatique via PAX
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            className={classes.sectionDescription}>
                                            Déclenchée après chaque encaissement au cours de la
                                            journée de marché
                                        </Typography>
                                    </div>
                                </div>
                                <SegmentedField
                                    name="autoPrintBill"
                                    value={autoPrintBill}
                                    onChange={e => setAutoPrintBill(e.target.value === 'true')}
                                    options={[
                                        { value: true, label: 'Oui' },
                                        { value: false, label: 'Non' }
                                    ]}
                                />
                            </div>

                            <Divider className={classes.divider} />

                            {/* Relevé d'identité bancaire */}
                            <div className={classes.section}>
                                <div className={classes.sectionHeader}>
                                    <div className={classes.sectionIcon}>
                                        <AccountBalanceOutlinedIcon fontSize="small" />
                                    </div>
                                    <div>
                                        <Typography className={classes.sectionTitle}>
                                            Relevé d'identité bancaire
                                        </Typography>
                                    </div>
                                </div>
                                <TextField
                                    onChange={e =>
                                        handleChange('bankIdentificationStatement', e.target.value)
                                    }
                                    placeholder="IBAN, BIC, nom de la banque..."
                                    multiline
                                    fullWidth
                                    value={bankIdentificationStatement ?? ''}
                                    rows={3}
                                    minRows={3}
                                    variant="outlined"
                                />
                            </div>

                            <Divider className={classes.divider} />

                            {/* Informations supplémentaires */}
                            <div className={classes.section}>
                                <div className={classes.sectionHeader}>
                                    <div className={classes.sectionIcon}>
                                        <NotesOutlinedIcon fontSize="small" />
                                    </div>
                                    <div>
                                        <Typography className={classes.sectionTitle}>
                                            Informations supplémentaires à faire apparaître sur la
                                            facture
                                        </Typography>
                                    </div>
                                </div>
                                <TextField
                                    onChange={e =>
                                        handleChange('additionalInformation', e.target.value)
                                    }
                                    placeholder="Mentions légales, conditions, coordonnées..."
                                    multiline
                                    fullWidth
                                    value={additionalInformation ?? ''}
                                    rows={8}
                                    minRows={8}
                                    variant="outlined"
                                />
                            </div>

                            <Divider className={classes.divider} />

                            {/* Tampon ou signature */}
                            <div className={classes.section}>
                                <div className={classes.sectionHeader}>
                                    <div className={classes.sectionIcon}>
                                        <CreateOutlinedIcon fontSize="small" />
                                    </div>
                                    <div>
                                        <Typography className={classes.sectionTitle}>
                                            Tampon ou signature de la ville
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            className={classes.sectionDescription}>
                                            Formats acceptés : PNG, JPG, JPEG
                                        </Typography>
                                    </div>
                                </div>

                                <FileInput
                                    file={signature}
                                    acceptedMimeType={'image/*'}
                                    setFile={handleFile}
                                />

                                {signature && (
                                    <Grid
                                        className={classes.previewContainer}
                                        container
                                        direction="column"
                                        alignItems="center"
                                        spacing={1}>
                                        <Grid item style={{ display: 'block', width: '100%' }}>
                                            <img
                                                className={classes.previewImg}
                                                id="output_1"
                                                alt=""
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Button
                                                href=""
                                                target="_blank"
                                                id="output_2"
                                                download="signature"
                                                component={Link}
                                                startIcon={<InsertDriveFileOutlinedIcon />}
                                                style={{ textTransform: 'none' }}
                                                color="primary">
                                                Ouvrir le fichier
                                            </Button>
                                        </Grid>
                                    </Grid>
                                )}
                            </div>

                            {/* Sticky-feeling footer action bar */}
                            <div className={classes.footer}>
                                {processing && <CircularProgress color="primary" size={22} />}
                                {message !== '' && (
                                    <Typography variant="body2" className={classes.message}>
                                        {message}
                                    </Typography>
                                )}
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={processing}
                                    variant="contained">
                                    Mettre à jour
                                </Button>
                            </div>
                        </Paper>
                    </form>
                )}
            </div>
        </div>
    );
};

export default BillConfig;
